import { defaultGcTime, defaultStaleTime } from '@/shared/constants';
import { isInvalid } from '@/shared/services/isInvalid';
import { useQuery } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';
import { localDayKey, seasonDayKey } from '../services/seasonDates';

export interface Season {
  id: number;
  name: string;
  seasonType: string;
  startDate: string;
  // Nullable in the schema: a season with no declared end is ongoing, and must
  // stay null rather than collapsing to new Date(null) → 1970-01-01.
  endDate: string | null;
  is_active: boolean;
}

export interface SeasonsResult {
  seasons: Season[];
  currentSeasonId: number | null;
}

export function getQueryKey(leagueId: number) {
  return ['Seasons', leagueId];
}

// startDate/endDate are mapped to Date objects by the queryFn but typed as string.
const time = (value: unknown): number => new Date(value as string).getTime();

// Display order for the dropdown: latest start date first. This used to be
// max(season.id) — an insertion-order proxy that put whichever row was seeded
// last on top, so a backfilled old season outranked the current one.
const sortSeasons = (seasons: Season[]): Season[] => {
  return [...seasons].sort((a, b) => {
    const aStart = time(a.startDate);
    const bStart = time(b.startDate);

    // Undated rows sink to the bottom instead of ordering unpredictably —
    // every NaN comparison returns false, which is not a valid comparator.
    if (Number.isNaN(aStart)) return Number.isNaN(bStart) ? b.id - a.id : 1;
    if (Number.isNaN(bStart)) return -1;

    // Same start date (Pre and Regular can share one): newest row first, the
    // previous behaviour, so the tie stays deterministic.
    return bStart - aStart || b.id - a.id;
  });
};

// Which season is "current" is a temporal question, so answer it with dates.
// This used to be max(season.id), an insertion-order proxy that just picked
// whichever row happened to be seeded last.
//
// 1. The season whose startDate..endDate contains today.
// 2. Off-season (today falls between seasons): the most recently ENDED season.
// 3. Nothing has started yet: the soonest to start.
//
// Compared as "yyyy-MM-dd" day keys, the same granularity the date strip uses
// to decide whether a season is running. Comparing Date.now() against a
// midnight endDate instead made a season stop being current at 00:00 on its
// own final day, so the two could name different seasons for a whole day.
const pickCurrentSeasonId = (seasons: Season[]): number | null => {
  if (!seasons.length) return null;

  const todayKey = localDayKey(new Date());
  const dated = seasons
    .map((season) => ({
      id: season.id,
      start: seasonDayKey(season.startDate),
      end: seasonDayKey(season.endDate),
    }))
    .filter((s): s is { id: number; start: string; end: string | null } => s.start !== null);

  if (!dated.length) return seasons[0].id;

  // A null end is an open-ended season, so it cannot have ended already.
  const inWindow = dated.filter((s) => s.start <= todayKey && (!s.end || todayKey <= s.end));
  if (inWindow.length) {
    // Overlapping windows (Pre bleeding into Regular): the later start wins.
    return inWindow.reduce((a, b) => (b.start > a.start ? b : a)).id;
  }

  const ended = dated.filter((s): s is { id: number; start: string; end: string } =>
    !!s.end && s.end < todayKey,
  );
  if (ended.length) {
    return ended.reduce((a, b) => (b.end > a.end ? b : a)).id;
  }

  return dated.reduce((a, b) => (b.start < a.start ? b : a)).id;
};

export const useListSeasonsQuery = (leagueId: number) => {
  const isValidQuery = !isInvalid(leagueId, 'number')


  return useQuery({
    queryKey: getQueryKey(leagueId),
    queryFn: async () => {
      const response = await get<Season[]>(`/v1.0/league/${leagueId}/seasons`);
      const mappedSeasons = response.data.map((season) => ({
        id: season.id,
        name: season.name,
        seasonType: season.seasonType,
        startDate: new Date(season.startDate),
        // Not new Date(null) — that is 1970-01-01 and truthy, which made an
        // open-ended season read as long finished everywhere downstream.
        endDate: season.endDate ? new Date(season.endDate) : null,
        is_active: season.is_active,
      })) as unknown as Season[];

      // Newest season on top, oldest at the bottom, by start date.
      const sortedSeasons = sortSeasons(mappedSeasons);

      const currentSeasonId = pickCurrentSeasonId(sortedSeasons);

      return {
        seasons: sortedSeasons,
        currentSeasonId,
      } as SeasonsResult;
    },
    staleTime: defaultStaleTime, // Data considered fresh for 30 seconds
    gcTime: defaultGcTime, // Data will be garbage collected after 5 minutes
    enabled: isValidQuery,
    refetchOnWindowFocus: isValidQuery, // refetch ONLY if enabled and leagueId is valid
    placeholderData: { seasons: [], currentSeasonId: null },
  });
}


export default useListSeasonsQuery;
